/**
 * Spacer Component
 *
 * A flexible spacing component that creates consistent whitespace
 * between elements. Can be used for fixed spacing or as a flex
 * grow element to push siblings apart.
 *
 * Uses CSS custom properties (design tokens) for spacing values,
 * ensuring consistency across the design system.
 *
 * @module components/core/Spacer
 *
 * @example
 * ```tsx
 * // Fixed vertical spacing (default)
 * <Stack>
 *   <Header />
 *   <Spacer size="lg" />
 *   <Content />
 * </Stack>
 *
 * // Fixed horizontal spacing
 * <HStack>
 *   <Logo />
 *   <Spacer size="md" axis="horizontal" />
 *   <Navigation />
 * </HStack>
 *
 * // Flex grow spacer (pushes elements apart)
 * <HStack>
 *   <Logo />
 *   <Spacer flex />
 *   <UserMenu />
 * </HStack>
 *
 * // Both axes (square spacer)
 * <Spacer size="xl" axis="both" />
 * ```
 */

import { type CSSProperties, forwardRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Spacer size options mapped to CSS custom properties
 */
export type SpacerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

/**
 * Spacer axis options
 */
export type SpacerAxis = 'horizontal' | 'vertical' | 'both';

/**
 * Props for the Spacer component
 */
export interface SpacerProps {
  /**
   * Size of the spacer - uses design system spacing tokens
   * @default 'md'
   */
  size?: SpacerSize;

  /**
   * Which axis the spacer applies to
   * - horizontal: Creates horizontal space (width)
   * - vertical: Creates vertical space (height)
   * - both: Creates a square spacer (width & height)
   * @default 'vertical'
   */
  axis?: SpacerAxis;

  /**
   * If true, the spacer will flex-grow to fill available space
   * When flex is true, size is ignored
   * @default false
   */
  flex?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Inline styles (use sparingly - prefer tokens)
   */
  style?: CSSProperties;

  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string;
}

/**
 * Map spacer sizes to CSS custom property values
 */
const sizeMap: Record<SpacerSize, string> = {
  xs: 'var(--spacing-xs)',
  sm: 'var(--spacing-sm)',
  md: 'var(--spacing-md)',
  lg: 'var(--spacing-lg)',
  xl: 'var(--spacing-xl)',
  '2xl': 'var(--spacing-2xl)',
  '3xl': 'var(--spacing-3xl)',
};

/**
 * Spacer Component
 *
 * Creates consistent whitespace between elements using design system tokens.
 * Can also be used as a flex grow element to push siblings apart.
 */
export const Spacer = forwardRef<HTMLDivElement, SpacerProps>(function Spacer(
  { size = 'md', axis = 'vertical', flex = false, className, style, 'data-testid': testId, ...restProps },
  ref
) {
  // If flex is true, render a flex-grow spacer
  if (flex) {
    return (
      <div
        ref={ref}
        className={cn('flex-grow', className)}
        style={style}
        aria-hidden="true"
        data-testid={testId}
        {...restProps}
      />
    );
  }

  // Build style based on axis
  const spacerStyle: CSSProperties = {
    ...(axis === 'vertical' || axis === 'both' ? { height: sizeMap[size], minHeight: sizeMap[size] } : {}),
    ...(axis === 'horizontal' || axis === 'both' ? { width: sizeMap[size], minWidth: sizeMap[size] } : {}),
    ...style,
  };

  return (
    <div
      ref={ref}
      className={cn(
        // Shrink behavior
        'flex-shrink-0',
        className
      )}
      style={spacerStyle}
      aria-hidden="true"
      data-testid={testId}
      {...restProps}
    />
  );
});

Spacer.displayName = 'Spacer';

/**
 * FlexSpacer Component
 *
 * A convenience component that renders a flex-grow spacer.
 * Useful for pushing elements to opposite ends of a flex container.
 *
 * @example
 * ```tsx
 * <HStack>
 *   <Logo />
 *   <FlexSpacer />
 *   <UserMenu />
 * </HStack>
 * ```
 */
export const FlexSpacer = forwardRef<HTMLDivElement, Omit<SpacerProps, 'flex' | 'size' | 'axis'>>(
  function FlexSpacer(props, ref) {
    return <Spacer ref={ref} flex {...props} />;
  }
);

FlexSpacer.displayName = 'FlexSpacer';

/**
 * VSpacer Component
 *
 * A convenience component for vertical spacing.
 *
 * @example
 * ```tsx
 * <VStack>
 *   <Header />
 *   <VSpacer size="xl" />
 *   <Content />
 * </VStack>
 * ```
 */
export const VSpacer = forwardRef<HTMLDivElement, Omit<SpacerProps, 'axis' | 'flex'>>(function VSpacer(props, ref) {
  return <Spacer ref={ref} axis="vertical" {...props} />;
});

VSpacer.displayName = 'VSpacer';

/**
 * HSpacer Component
 *
 * A convenience component for horizontal spacing.
 *
 * @example
 * ```tsx
 * <HStack>
 *   <Icon />
 *   <HSpacer size="sm" />
 *   <Text>Label</Text>
 * </HStack>
 * ```
 */
export const HSpacer = forwardRef<HTMLDivElement, Omit<SpacerProps, 'axis' | 'flex'>>(function HSpacer(props, ref) {
  return <Spacer ref={ref} axis="horizontal" {...props} />;
});

HSpacer.displayName = 'HSpacer';

export default Spacer;
